import React, { useCallback, useEffect, useState } from "react";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import MainLayout from "../../components/layout/MainLayout";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./ArchivedEmployees.css";

function ArchivedEmployees() {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(10);
  const [deleting, setDeleting] = useState(null);

  const fetchArchivedEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/archived-employees", {
        params: { page, search, per_page: perPage },
      });

      if (response.data.success) {
        setEmployees(response.data.data);
        setLastPage(response.data.last_page);
        setTotal(response.data.total);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load archived employees",
      });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search]);

  useEffect(() => {
    fetchArchivedEmployees();
  }, [fetchArchivedEmployees]);

  const clearSearch = () => {
    setSearch("");
    setPage(1);
  };

  const handleDelete = (id, name) => {
    Swal.fire({
      title: "Permanently Delete?",
      html: `<p style="color:#374151;font-size:14px"><strong>${name}</strong> will be permanently removed.<br/>This action <strong>cannot be undone</strong>.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete Permanently",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) {
        return;
      }

      setDeleting(id);
      try {
        await api.delete(`/archived-employees/${id}`);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: `${name} has been permanently removed.`,
          timer: 2000,
          showConfirmButton: false,
        });
        setEmployees((prev) => prev.filter((employee) => employee.id !== id));
        setTotal((prev) => prev - 1);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to delete employee",
        });
      } finally {
        setDeleting(null);
      }
    });
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

  const renderPagination = () => {
    if (lastPage <= 1) {
      return null;
    }

    return (
      <div className="ae-pagination">
        <span className="ae-pagination-info">
          Showing {(page - 1) * perPage + 1}-
          {Math.min(page * perPage, total)} of {total}
        </span>
        <div className="ae-pagination-btns">
          <button
            className="ae-page-btn"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page === 1}
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          {Array.from({ length: lastPage }, (_, index) => index + 1).map(
            (pageNumber) => (
              <button
                key={pageNumber}
                className={`ae-page-btn ${page === pageNumber ? "active" : ""}`}
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ),
          )}
          <button
            className="ae-page-btn"
            onClick={() => setPage((current) => Math.min(lastPage, current + 1))}
            disabled={page === lastPage}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    );
  };

  const archivedEmployeeColumns = [
    {
      header: "#",
      render: (_, index) => (
        <span className="ae-row-num">{(page - 1) * perPage + index + 1}</span>
      ),
    },
    {
      header: "Emp. ID",
      render: (employee) => (
        <span className="ae-emp-code">{employee.code}</span>
      ),
    },
    {
      header: "Employee",
      render: (employee) => (
        <div className="ae-emp-cell">
          <div className="ae-avatar">
            {employee.profile_picture ? (
              <img src={employee.profile_picture} alt={employee.name} />
            ) : (
              <span className="ae-avatar-initials">
                {getInitials(employee.name)}
              </span>
            )}
            <span className="ae-inactive-dot"></span>
          </div>
          <div className="ae-emp-info">
            <span className="ae-emp-name">{employee.name}</span>
            <span className="ae-emp-email">{employee.email}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Department",
      render: (employee) => (
        <span className="ae-dept-badge">{employee.department}</span>
      ),
    },
    {
      header: "Designation",
      render: (employee) => (
        <span className="ae-designation">{employee.designation}</span>
      ),
    },
    {
      header: "Contact",
      render: (employee) => (
        <span className="ae-contact">{employee.phone_number || "-"}</span>
      ),
    },
    {
      header: "Joined",
      render: (employee) => <span className="ae-date">{employee.hired_date}</span>,
    },
    {
      header: "Archived On",
      render: (employee) => (
        <div className="ae-archived-date">
          <i className="fas fa-calendar-times"></i>
          <span className="ae-date">{employee.archived_at}</span>
        </div>
      ),
    },
    {
      header: "Action",
      render: (employee) => (
        <CommonActionMenu
          ariaLabel={`Actions for archived employee ${employee.code}`}
          actions={[
            {
              label: deleting === employee.id ? "Deleting..." : "Delete",
              icon:
                deleting === employee.id
                  ? "fas fa-spinner fa-spin"
                  : "fas fa-trash-alt",
              variant: "danger",
              disabled: deleting === employee.id,
              onClick: () => handleDelete(employee.id, employee.name),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="ae-container">
        <div className="ae-page-header">
          <div className="ae-header-top">
            <div className="ae-header-title-row">
              <div>
                <h4 className="ae-page-title">
                  <i className="fas fa-archive"></i>
                  Archived Employees
                </h4>
                <p className="ae-page-subtitle">
                  {total} archived record{total !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>

          <div className="ae-info-banner">
            <i className="fas fa-info-circle"></i>
            <span>
              Archived employees are <strong>inactive</strong> and excluded from
              payroll, attendance and all active workflows.
            </span>
          </div>
        </div>

        <div className="ae-card">
          <div className="ae-toolbar">
            <div className="ae-toolbar-left">
              <div className="ae-stat-chip">
                <i className="fas fa-user-slash"></i>
                <span>{total} Archived</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="ae-loading">
              <div className="ae-spinner"></div>
              <p>Loading archived employees...</p>
            </div>
          ) : employees.length === 0 ? (
            <div className="ae-empty">
              <div className="ae-empty-icon">
                <i className="fas fa-archive"></i>
              </div>
              <h5>No archived employees</h5>
              <p>
                {search
                  ? `No results for "${search}"`
                  : "Deactivated employees will appear here."}
              </p>
              {search && (
                <button className="ae-btn-outline" onClick={clearSearch}>
                  <i className="fas fa-times"></i> Clear Search
                </button>
              )}
            </div>
          ) : (
            <>
              <CommonTable
                columns={archivedEmployeeColumns}
                data={employees}
                tableClassName="ae-table"
                wrapperClassName="ae-table-wrap"
                getRowClassName={(employee) =>
                  deleting === employee.id ? "ae-row-deleting" : ""
                }
              />
              {renderPagination()}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

export default ArchivedEmployees;
